/**
 * Goodsole GWP — Theme-only Gift With Purchase
 */
(function () {
  "use strict";

  var PROP_KEY = "_gwp";
  var PROP_VAL = "true";
  var tiers = [];
  var syncing = false;
  var syncPromise = Promise.resolve();
  var gwpDepth = 0;
  var lastRender = null;
  var availability = {}; // variantId → boolean

  // Cached DOM refs — one set per instance (drawer, page)
  var domSets = [];
  var domCached = false;

  function init(config) {
    tiers = (config.tiers || []).slice().sort(function (a, b) {
      return a.threshold - b.threshold;
    });
    if (!tiers.length) return;
    document.addEventListener("cart:updated", function (e) {
      // Theme includes cart data in event — use it to skip a /cart.js fetch
      var cart = e.detail && e.detail.cart;
      syncGwp(cart);
    });
    interceptCartFetch();
    interceptCheckout();
    fetchAvailability().then(function () { syncGwp(); });
  }

  function fetchAvailability() {
    var handles = {};
    tiers.forEach(function (tier) {
      tier.gifts.forEach(function (gift) {
        if (gift.productHandle) handles[gift.productHandle] = true;
      });
    });

    var fetches = Object.keys(handles).map(function (handle) {
      return fetch("/products/" + handle + ".js")
        .then(function (r) { return r.json(); })
        .then(function (product) {
          (product.variants || []).forEach(function (v) {
            availability[String(v.id)] = v.available;
          });
        })
        .catch(function () { /* product not found */ });
    });

    return Promise.all(fetches);
  }

  function getCart() {
    return fetch("/cart.js").then(function (r) { return r.json(); });
  }

  function cartTotalExcludingGwp(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (item.properties && item.properties[PROP_KEY] === PROP_VAL) return;
      total += item.final_line_price;
    });
    return total / 100;
  }

  function gwpItemsInCart(cart) {
    return cart.items.filter(function (item) {
      return item.properties && item.properties[PROP_KEY] === PROP_VAL;
    });
  }

  function addGift(variantId) {
    gwpDepth++;
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: { [PROP_KEY]: PROP_VAL } }]
      })
    }).then(function (r) {
      if (!r.ok) console.error("GWP add failed", r.status);
      return r.ok;
    }).finally(function () { gwpDepth--; });
  }

  function removeGift(variantId) {
    gwpDepth++;
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: String(variantId), quantity: 0, properties: { [PROP_KEY]: PROP_VAL } })
    }).finally(function () { gwpDepth--; });
  }

  function checkEligibility(cartTotal) {
    var unlocked = [];
    var next = null;
    tiers.forEach(function (tier) {
      if (cartTotal >= tier.threshold) unlocked.push(tier);
      else if (!next) next = tier;
    });
    return { unlockedTiers: unlocked, nextTier: next, allTiers: tiers };
  }

  // ── Gift click: optimistic UI, fire-and-forget network ─────────

  function onGiftAdd(gift) {
    if (lastRender) {
      lastRender.currentGifts.push({
        variantId: String(gift.variantId),
        title: gift.title,
        variantTitle: null,
        image: gift.imageUrl
      });
      renderProgressBar(lastRender);
    }
    // Network in background — no sync needed, optimistic UI is already correct
    addGift(gift.variantId);
  }

  function onGiftRemove(variantId) {
    if (lastRender) {
      lastRender.currentGifts = lastRender.currentGifts.filter(function (g) {
        return g.variantId !== String(variantId);
      });
      renderProgressBar(lastRender);
    }
    removeGift(variantId);
  }

  // ── Sync: single getCart call ───────────────────────────────────

  function syncGwp(cartData) {
    if (syncing) return syncPromise;
    syncing = true;

    // Use provided cart data or fetch fresh
    var cartReady = cartData ? Promise.resolve(cartData) : getCart();

    syncPromise = cartReady.then(function (cart) {
      var cartTotal = cartTotalExcludingGwp(cart);
      var gwpItems = gwpItemsInCart(cart);
      var result = checkEligibility(cartTotal);

      var allowed = {};
      result.unlockedTiers.forEach(function (tier) {
        tier.gifts.forEach(function (g) { allowed[String(g.variantId)] = true; });
      });

      // Determine what's eligible right now
      var currentGifts = [];
      gwpItems.forEach(function (item) {
        if (allowed[String(item.variant_id)]) {
          currentGifts.push({
            variantId: String(item.variant_id),
            title: item.product_title,
            variantTitle: item.variant_title,
            image: item.image
          });
        }
      });

      // Render immediately with correct state — before any cleanup calls
      var renderData = {
        cartTotal: cartTotal,
        unlockedTiers: result.unlockedTiers,
        nextTier: result.nextTier,
        allTiers: result.allTiers,
        currentGifts: currentGifts
      };
      lastRender = renderData;
      renderProgressBar(renderData);

      // Clean up ineligible gifts & fix qty in background
      var toRemove = [];
      var toFix = [];
      gwpItems.forEach(function (item) {
        var vid = String(item.variant_id);
        if (!allowed[vid]) toRemove.push(vid);
        else if (item.quantity > 1) toFix.push(item.key);
      });

      // Fire all removals in parallel
      var ops = toRemove.map(function (vid) { return removeGift(vid); });
      toFix.forEach(function (key) {
        gwpDepth++;
        ops.push(
          fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: key, quantity: 1 })
          }).finally(function () { gwpDepth--; })
        );
      });

      if (ops.length) {
        return Promise.all(ops).then(function () {
          document.dispatchEvent(new CustomEvent("cart:build"));
        });
      }
    }).catch(function (err) {
      console.error("GWP sync error:", err);
    }).finally(function () {
      syncing = false;
    });

    return syncPromise;
  }

  function interceptCartFetch() {
    var original = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === "string" ? arguments[0] : (arguments[0] && arguments[0].url) || "";
      return original.apply(this, arguments).then(function (response) {
        if (gwpDepth === 0 &&
            (url.indexOf("/cart/add") !== -1 || url.indexOf("/cart/update") !== -1 ||
             url.indexOf("/cart/change") !== -1 || url.indexOf("/cart/clear") !== -1)) {
          setTimeout(function () { syncGwp(); }, 100);
        }
        return response;
      });
    };
  }

  function interceptCheckout() {
    document.addEventListener("click", function (e) {
      var link = e.target.closest('a[href$="/checkout"]') ||
                 e.target.closest('a[href*="/checkout?"]') ||
                 e.target.closest('[name="checkout"]');
      if (!link) return;
      e.preventDefault();
      e.stopPropagation();
      syncGwp().then(function () { window.location.href = "/checkout"; })
               .catch(function () { window.location.href = "/checkout"; });
    }, true);
  }

  // ── Rendering ───────────────────────────────────────────────────

  function cacheDom() {
    var suffixes = ["", "-page"];
    domSets = [];
    suffixes.forEach(function (s) {
      var container = document.getElementById("GwpProgressBar" + s);
      if (container) {
        domSets.push({
          container: container,
          barFill: document.getElementById("GwpBarFill" + s),
          milestones: document.getElementById("GwpMilestones" + s),
          message: document.getElementById("GwpMessage" + s),
          picker: document.getElementById("GwpPicker" + s),
          selected: document.getElementById("GwpSelected" + s)
        });
      }
    });
    domCached = true;
    return domSets.length > 0;
  }

  function renderProgressBar(detail) {
    if (!domCached && !cacheDom()) return;
    if (!domSets.length) return;
    domSets.forEach(function (dom) { renderProgressBarInto(dom, detail); });
  }

  function renderProgressBarInto(dom, detail) {

    var allTiers = detail.allTiers || [];
    var unlocked = detail.unlockedTiers || [];
    var nextTier = detail.nextTier;
    var cartTotal = detail.cartTotal || 0;

    if (!allTiers.length) return;
    dom.container.style.display = "";

    // Build a set of variant IDs in cart for quick lookup
    var inCartSet = {};
    detail.currentGifts.forEach(function (g) { inCartSet[g.variantId] = g; });

    // Check which tiers already have a gift selected
    var tierHasGift = {};
    unlocked.forEach(function (tier) {
      tier.gifts.forEach(function (gift) {
        if (inCartSet[String(gift.variantId)]) {
          tierHasGift[tier.threshold] = {
            gift: gift,
            cartGift: inCartSet[String(gift.variantId)]
          };
        }
      });
    });

    // Progress bar
    var max = allTiers[allTiers.length - 1].threshold;
    dom.barFill.style.width = Math.min((cartTotal / max) * 100, 100) + "%";

    // Milestones
    dom.milestones.innerHTML = "";
    allTiers.forEach(function (tier) {
      var dot = document.createElement("div");
      dot.className = "gwp-progress__milestone" +
        (cartTotal >= tier.threshold ? " gwp-progress__milestone--unlocked" : "");
      dot.style.left = ((tier.threshold / max) * 100) + "%";
      dot.title = "$" + tier.threshold + " \u2014 " + tier.name;
      dom.milestones.appendChild(dot);
    });

    // Calculate total unlocked gift value (use highest retailValue per tier as the representative value)
    var totalGiftValue = 0;
    unlocked.forEach(function (tier) {
      var maxVal = 0;
      tier.gifts.forEach(function (g) {
        if (g.retailValue && g.retailValue > maxVal) maxVal = g.retailValue;
      });
      totalGiftValue += maxVal;
    });

    // Message
    if (nextTier) {
      var remaining = Math.ceil(nextTier.threshold - cartTotal);
      var valueStr = totalGiftValue > 0 ? " <span class='gwp-progress__total-value'>$" + totalGiftValue.toFixed(2) + " in free gifts unlocked</span>" : "";
      dom.message.innerHTML = "You\u2019re <strong>$" + remaining + " away</strong> from free <strong>" + nextTier.name + "</strong>" + valueStr;
    } else {
      dom.message.innerHTML = "You\u2019ve unlocked <strong>all free gifts</strong> \u2014 worth <strong>$" + totalGiftValue.toFixed(2) + "</strong>!";
    }

    // Picker: only show for unlocked tiers that DON'T have a gift selected yet
    var tiersNeedingPick = unlocked.filter(function (tier) {
      return !tierHasGift[tier.threshold];
    });

    if (tiersNeedingPick.length) {
      dom.picker.style.display = "";
      dom.picker.innerHTML = "";
      tiersNeedingPick.forEach(function (tier) {
        var tierHasStock = tier.gifts.some(function (g) {
          return availability[String(g.variantId)] !== false;
        });

        var tierEl = document.createElement("div");
        tierEl.className = "gwp-progress__picker-tier";

        // Collapsed trigger button
        var trigger = document.createElement("button");
        trigger.type = "button";
        trigger.className = "gwp-progress__picker-trigger" + (tierHasStock ? "" : " gwp-progress__picker-trigger--oos");
        var tierMaxVal = 0;
        tier.gifts.forEach(function (g) {
          if (g.retailValue && g.retailValue > tierMaxVal) tierMaxVal = g.retailValue;
        });

        if (tierHasStock) {
          trigger.innerHTML = "Choose your free " + tier.name +
            (tierMaxVal > 0 ? " <span class='gwp-progress__picker-value'>Worth up to $" + tierMaxVal.toFixed(2) + "</span>" : "") +
            " <span class='gwp-progress__picker-arrow'>\u25B8</span>";
        } else {
          trigger.innerHTML = tier.name + " \u2014 currently out of stock";
          trigger.disabled = true;
        }
        tierEl.appendChild(trigger);

        // Gift cards container — hidden by default
        var wrap = document.createElement("div");
        wrap.className = "gwp-progress__picker-gifts";
        wrap.style.display = "none";

        trigger.addEventListener("click", function (e) {
          e.stopPropagation();
          var isOpen = wrap.style.display !== "none";
          wrap.style.display = isOpen ? "none" : "";
          trigger.classList.toggle("gwp-progress__picker-trigger--open", !isOpen);
        });

        tier.gifts.forEach(function (gift) {
          var isOos = availability[String(gift.variantId)] === false;
          var card = document.createElement("button");
          card.type = "button";
          card.className = "gwp-progress__gift-card" + (isOos ? " gwp-progress__gift-card--oos" : "");
          if (isOos) card.disabled = true;

          if (gift.imageUrl) {
            var img = document.createElement("img");
            img.className = "gwp-progress__gift-img";
            img.src = gift.imageUrl;
            img.alt = gift.title;
            card.appendChild(img);
          }

          var info = document.createElement("div");
          info.className = "gwp-progress__gift-info";
          var title = document.createElement("div");
          title.className = "gwp-progress__gift-title";
          title.textContent = gift.title;
          info.appendChild(title);
          if (gift.retailValue) {
            var val = document.createElement("div");
            val.className = "gwp-progress__gift-value";
            val.textContent = "Worth $" + gift.retailValue.toFixed(2);
            info.appendChild(val);
          }
          var action = document.createElement("div");
          if (isOos) {
            action.className = "gwp-progress__gift-oos";
            action.textContent = "Out of stock";
          } else {
            action.className = "gwp-progress__gift-select";
            action.textContent = "Add to cart";
          }
          info.appendChild(action);
          card.appendChild(info);

          if (!isOos) {
            card.addEventListener("click", function (e) {
              e.stopPropagation();
              onGiftAdd(gift);
            });
          }
          wrap.appendChild(card);
        });

        tierEl.appendChild(wrap);
        dom.picker.appendChild(tierEl);
      });
    } else {
      dom.picker.style.display = "none";
      dom.picker.innerHTML = "";
    }

    // Selected gifts summary (with Change button)
    var selectedGifts = [];
    unlocked.forEach(function (tier) {
      var match = tierHasGift[tier.threshold];
      if (match) {
        selectedGifts.push({ tier: tier, gift: match.gift, cartGift: match.cartGift });
      }
    });

    if (selectedGifts.length) {
      dom.selected.style.display = "";
      dom.selected.innerHTML = "";
      selectedGifts.forEach(function (s) {
        var row = document.createElement("div");
        row.className = "gwp-progress__selected-item";
        var info = document.createElement("div");
        info.className = "gwp-progress__selected-info";

        if (s.cartGift.image) {
          var thumb = document.createElement("img");
          thumb.className = "gwp-progress__selected-img";
          thumb.src = s.cartGift.image;
          thumb.alt = s.cartGift.title;
          info.appendChild(thumb);
        }

        var badge = document.createElement("span");
        badge.className = "gwp-progress__selected-badge";
        badge.textContent = "FREE";
        info.appendChild(badge);

        var name = document.createElement("span");
        name.className = "gwp-progress__selected-title";
        name.textContent = s.cartGift.title;
        info.appendChild(name);

        if (s.gift.retailValue) {
          var val = document.createElement("span");
          val.className = "gwp-progress__selected-value";
          val.textContent = "Worth $" + s.gift.retailValue.toFixed(2);
          info.appendChild(val);
        }

        row.appendChild(info);

        var changeBtn = document.createElement("button");
        changeBtn.type = "button";
        changeBtn.className = "gwp-progress__change-btn";
        changeBtn.textContent = "Change";
        changeBtn.addEventListener("click", function (e) {
          e.stopPropagation();
          onGiftRemove(s.gift.variantId);
        });
        row.appendChild(changeBtn);

        dom.selected.appendChild(row);
      });
    } else {
      dom.selected.style.display = "none";
    }
  }

  window.GoodsoleGWP = { init: init, addGift: addGift, removeGift: removeGift, syncGwp: syncGwp };
})();
